import React from "react";

// Shared football-shirt SVG used by both the Lineup Builder and the
// Classic Lineup Builder. `showNumber` lets the Classic builder render
// shirts with no number printed on them (name tag underneath still shows).
//
// `design` drives the shirt's pattern/colourway (Classic builder's shirt
// selectors only) — pass either the string "classic" (default, renders
// pixel-identical to the original/unchanged shirt) or a design object
// `{ pattern_type, primary_color, secondary_color, trim_color, image_url }`
// as returned by `GET /api/classic-shirt-designs`. The goalkeeper slot
// uses its own, separate `role: "goalkeeper"` designs (picked via the
// Classic Lineup Builder's dedicated GK shirt selector) — passing an
// outfield design to a GK slot, or vice versa, is the caller's job to
// avoid; ShirtIcon itself renders whatever `design` it's given. With no
// design (or "classic"), the keeper still renders the original plain
// green shirt, unchanged from before this selector existed.
const CLASSIC_DEFAULT = { pattern_type: "classic_default" };

function OutfieldFill({ design }) {
  const d = !design || design === "classic" ? CLASSIC_DEFAULT : design;
  const primary = d.primary_color || "#6CABDD";
  const secondary = d.secondary_color || "#FFFFFF";

  switch (d.pattern_type) {
    case "stripes_full":
      // Full-height alternating vertical stripes (e.g. '87 Wembley Stripes,
      // '97 Home Stripes, Third Stripes) — same renderer, different colors.
      return (
        <>
          <rect x="0" y="0" width="100" height="100" fill={secondary} />
          <rect x="15" y="0" width="10" height="86" fill={primary} />
          <rect x="30" y="0" width="10" height="86" fill={primary} />
          <rect x="45" y="0" width="10" height="86" fill={primary} />
          <rect x="60" y="0" width="10" height="86" fill={primary} />
          <rect x="75" y="0" width="10" height="86" fill={primary} />
          <path d="M15,25 L35,15 L40,15 L45,19 L35,25 Z" fill={primary} />
          <path d="M85,25 L65,15 L60,15 L55,19 L65,25 Z" fill={primary} />
        </>
      );
    case "checkerboard": {
      // Alternating primary/secondary squares (e.g. the '90s Peugeot
      // away checkerboard kit).
      const cols = 6;
      const rows = 8;
      const cw = 100 / cols;
      const ch = 100 / rows;
      const squares = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          squares.push(
            <rect
              key={`${r}-${c}`}
              x={c * cw}
              y={r * ch}
              width={cw}
              height={ch}
              fill={(r + c) % 2 === 0 ? primary : secondary}
            />
          );
        }
      }
      return (
        <>
          {squares}
          <path d="M15,25 L35,15 L40,15 L45,19 L35,25 Z" fill={primary} />
          <path d="M85,25 L65,15 L60,15 L55,19 L65,25 Z" fill={primary} />
        </>
      );
    }
    case "chevron_sides":
      // Primary base with secondary chevron/side-panel accents running
      // down both sides (e.g. the brown/white Admiral-style away kit).
      return (
        <>
          <rect x="0" y="0" width="100" height="100" fill={primary} />
          <path d="M27,20 L37,43 L33,85 L23,85 L27,43 Z" fill={secondary} />
          <path d="M31,20 L41,43 L37,85 L31,85 L35,43 L29,22 Z" fill={primary} />
          <path d="M73,20 L63,43 L67,85 L77,85 L73,43 Z" fill={secondary} />
          <path d="M69,20 L59,43 L63,85 L69,85 L65,43 L71,22 Z" fill={primary} />
          <path d="M15,25 L35,15 L40,15 L45,19 L35,25 Z" fill={primary} />
          <path d="M85,25 L65,15 L60,15 L55,19 L65,25 Z" fill={primary} />
        </>
      );
    case "ringer":
      // Plain primary body — the trim color does the talking on the
      // collar/cuffs (handled in the outer outline below).
      return (
        <>
          <rect x="0" y="0" width="100" height="100" fill={primary} />
          <path d="M15,25 L35,15 L40,15 L45,19 L35,25 Z" fill={primary} />
          <path d="M85,25 L65,15 L60,15 L55,19 L65,25 Z" fill={primary} />
        </>
      );
    case "custom_image":
      // Admin-uploaded SVG/image (Admin → Classic Shirts). Sliced to fill
      // the shirt silhouette; falls back to a plain primary body if no
      // image has been uploaded yet.
      return d.image_url ? (
        <image
          href={d.image_url}
          x="0"
          y="0"
          width="100"
          height="100"
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <rect x="0" y="0" width="100" height="100" fill={primary} />
      );
    case "pinstripe":
      // Thin secondary pinstripes on a primary base.
      return (
        <>
          <rect x="0" y="0" width="100" height="100" fill={primary} />
          {[16, 24, 32, 40, 48, 56, 64, 72, 80].map((x) => (
            <rect key={x} x={x} y="0" width="2" height="86" fill={secondary} opacity="0.6" />
          ))}
          <path d="M15,25 L35,15 L40,15 L45,19 L35,25 Z" fill={primary} />
          <path d="M85,25 L65,15 L60,15 L55,19 L65,25 Z" fill={primary} />
        </>
      );
    case "classic_default":
    default:
      // Untouched original design — pixel-identical regardless of any
      // color props, so the required default never changes.
      return (
        <>
          <rect x="0" y="0" width="100" height="100" fill="#FFF" />
          <rect x="15" y="44" width="10" height="42" fill="#6CABDD" />
          <rect x="30" y="44" width="10" height="42" fill="#6CABDD" />
          <rect x="45" y="44" width="10" height="42" fill="#6CABDD" />
          <rect x="60" y="44" width="10" height="42" fill="#6CABDD" />
          <rect x="75" y="44" width="10" height="42" fill="#6CABDD" />
          <rect x="0" y="0" width="100" height="44" fill="#6CABDD" />
          <path d="M15,25 L35,15 L40,15 L45,19 L35,25 Z" fill="#6CABDD" stroke="#6CABDD" strokeWidth="1" />
          <path d="M85,25 L65,15 L60,15 L55,19 L65,25 Z" fill="#6CABDD" stroke="#6CABDD" strokeWidth="1" />
        </>
      );
  }
}

export const ShirtIcon = ({ isGk = false, number = "", sizeClass = "h-14 w-14", showNumber = true, design = "classic" }) => {
  const primaryColor = isGk ? "#10B981" : "#6CABDD"; // emerald vs sky blue
  const trimColor = (!isGk && design && design !== "classic" && design.trim_color) || "#002d75";
  return (
    <svg viewBox="0 0 100 100" className={`${sizeClass} drop-shadow-md select-none pointer-events-none`}>
      <defs>
        <clipPath id="shirt-clip-path">
          <path d="M15,25 L35,15 L40,15 L50,23 L60,15 L65,15 L85,25 L85,40 L73,43 L73,85 L27,85 L27,43 L15,40 Z" />
        </clipPath>
      </defs>

      {isGk ? (
        // Keeper Shirt — plain green by default (unchanged from before),
        // or an admin-uploaded `role: "goalkeeper"` design when one is
        // picked in the builder's dedicated GK shirt selector.
        <g>
          <g clipPath="url(#shirt-clip-path)">
            {!design || design === "classic" ? (
              <rect x="0" y="0" width="100" height="100" fill={primaryColor} />
            ) : (
              <OutfieldFill design={design} />
            )}
          </g>
          <path d="M15,25 L35,15 L40,15 L50,23 L60,15 L65,15 L85,25 L85,40 L73,43 L73,85 L27,85 L27,43 L15,40 Z" fill="none" stroke="#FFF" strokeWidth="2.5" />
          <path d="M15,25 L35,15 L40,15 L50,23 L60,15 L65,15 L85,25 L85,40 L73,43 L73,85 L27,85 L27,43 L15,40 Z" fill="none" stroke="#002d75" strokeWidth="2.5" />
          <path d="M40,15 L50,25 L60,15" fill="none" stroke="#002d75" strokeWidth="2.5" />
          {showNumber ? (
            <text x="50" y="46" fill="#FFF" stroke="#002d75" strokeWidth="1.5" paintOrder="stroke" fontSize="24" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" alignmentBaseline="middle">
              {number}
            </text>
          ) : null}
        </g>
      ) : (
        // Outfield Shirt — pattern/colourway driven by `design`.
        <g>
          <g clipPath="url(#shirt-clip-path)">
            <OutfieldFill design={design} />
          </g>
          {/* Outer outline + collar trim */}
          <path d="M15,25 L35,15 L40,15 L50,23 L60,15 L65,15 L85,25 L85,40 L73,43 L73,85 L27,85 L27,43 L15,40 Z" fill="none" stroke={trimColor} strokeWidth="2.5" />
          <path d="M40,15 L50,25 L60,15" fill="none" stroke={trimColor} strokeWidth="2.5" />
          {showNumber ? (
            <text x="50" y="36" fill="#FFF" fontSize="23" fontWeight="900" fontFamily="sans-serif" textAnchor="middle" alignmentBaseline="middle">
              {number}
            </text>
          ) : null}
        </g>
      )}
    </svg>
  );
};

export default ShirtIcon;
